import React, { Fragment, useEffect, useState } from 'react'
import { Typography, Button, Box, Grid } from '@mui/material'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import {
  cancelSingleOrder,
  cancelMultipleOrders,
  getOrderDetails
} from '../config/api'
import { generateErrorFromResponse } from '../../../utils/errorHelper'
import { Order } from '../../../interfaces/Order/Order'
import { ErrorResponse } from '../../../interfaces'

interface OrderCancellationProps {
  orderIds: number[]
  orders: Order[]
  onSuccess: (message: string) => void
  onError: (message: string) => void
  onClose: () => void
}

export const OrderCancellation: React.FC<OrderCancellationProps> = ({
  orderIds,
  orders,
  onSuccess,
  onError,
  onClose
}) => {
  // Estado para guardar la referencia del pedido único
  const [orderReference, setOrderReference] = useState<string>('')

  // 1. Eliminamos el 'useApi' duplicado. Usamos estos dos.
  const { state: cancelState, callApi: callCancelApi } = useApi()
  const { state: fetchState, callApi: callFetchApi } = useApi<Order>()

  // Esta función SÍ se usa para el modo de múltiples pedidos
  const getOrderReferenceId = (orderId: number): string => {
    const order = orders.find((o) => o.id === orderId)
    return order ? order.referenceId.toString() : orderId.toString()
  }

  // 2. CORRECCIÓN: Usar 'callCancelApi'
  const handleSingleOrderCancellation = async (orderId: number) => {
    try {
      await callCancelApi(cancelSingleOrder(orderId))
      onSuccess('Solicitud de cancelación realizada correctamente')
      onClose()
    } catch (error) {
      onError(generateErrorFromResponse(error as { data: ErrorResponse }))
      onClose()
    }
  }

  // 2. CORRECCIÓN: Usar 'callCancelApi'
  const handleMultipleOrderCancellation = async () => {
    try {
      await callCancelApi(cancelMultipleOrders(orderIds))
      onSuccess('Solicitud de cancelación agendada correctamente')
      onClose()
    } catch (error) {
      onError(generateErrorFromResponse(error as { data: ErrorResponse }))
      onClose()
    }
  }

  const handleConfirm = () => {
    if (orderIds.length === 1) {
      handleSingleOrderCancellation(orderIds[0])
    } else {
      handleMultipleOrderCancellation()
    }
  }

  useEffect(() => {
    if (orderReference) return

    if (orderIds.length === 1) {
      const orderId = orderIds[0]
      
      if (!orderId) return

      const localOrder = orders.find((o) => o.id === orderId)

      if (localOrder) {
        setOrderReference(localOrder.referenceId.toString())
      } else {
        const fetchOrder = async () => {
          try {
            const response = await callFetchApi(getOrderDetails(orderId))
            if (response.data) {
              setOrderReference(response.data.referenceId.toString())
            } else {
              setOrderReference(orderId.toString())
            }
          } catch (e) {
            console.error('Error buscando detalles del pedido:', e)
            setOrderReference(orderId.toString())
          }
        }
        fetchOrder()
      }
    }
  }, [orderIds, orders, callFetchApi, orderReference])

  const modalText: string =
    orderIds.length === 1
      ? `Esta seguro que desea cancelar el pedido ${
          orderReference || '...'
        } seleccionado, esta acción no podrá ser reversada`
      : `Esta seguro que desea cancelar los ${orderIds.length} pedidos seleccionados, esta acción no podrá ser reversada.`

  const isLoading =
    cancelState.status === Status.Loading ||
    fetchState.status === Status.Loading

  return (
    <Fragment>
      <Box p={3}>
        <Typography variant='h5' gutterBottom>
          Cancelación de Pedido
        </Typography>

        <Box mt={2} mb={3}>
          <Typography variant='body1'>{modalText}</Typography>
        </Box>

        {/*Usar 'orderReference' del estado */}
        {orderIds.length === 1 && (
          <Box mt={2} mb={2}>
            <Typography variant='body2' color='textSecondary'>
              <strong>ID de Pedido:</strong> {orderReference || 'Buscando...'}
            </Typography>
          </Box>
        )}

        {orderIds.length > 1 && (
          <Box mt={2} mb={2}>
            <Typography variant='body2' color='textSecondary'>
              <strong>Cantidad de pedidos seleccionados:</strong>{' '}
              {orderIds.length}
            </Typography>
            <Typography variant='caption' color='textSecondary'>
              <strong>
                IDs de Pedidos:{' '}
                {orderIds
                  .slice(0, 5)
                  .map((id) => getOrderReferenceId(id))
                  .join(', ')}
                {orderIds.length > 5 && '...'}
              </strong>
            </Typography>
          </Box>
        )}

        <Grid
          container
          spacing={2}
          justifyContent='flex-end'
          style={{ marginTop: '20px' }}
        >
          <Grid item>
            <Button
              type='button'
              variant='outlined'
              color='secondary'
              onClick={onClose}
              disabled={isLoading}
            >
              Salir
            </Button>
          </Grid>
          <Grid item>
            <Button
              type='button'
              variant='contained'
              color='primary'
              onClick={handleConfirm}
              disabled={isLoading}
            >
              {isLoading ? 'Procesando...' : 'Cancelar Pedidos'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Fragment>
  )
}